import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { CiiRanges } from "@app/services/api/api.models";

@Component({
  selector: "app-cii-rating-gauge-diagramm",
  templateUrl: "./cii-rating-gauge-diagramm.component.html",
  styleUrls: ["./cii-rating-gauge-diagramm.component.scss"],
})
export class CiiRatingGaugeDiagrammComponent implements OnChanges {
  @Input() aer_gtnm: number;
  @Input() ciiRating: string;
  @Input() ciiRangesCurrentYear: CiiRanges;
  ciiRatingChartData: any;
  ciiRatingChartOptions: any;
  ciiRatingChartPlugins: any;

  chartReady = false;

  ngOnChanges(changes: SimpleChanges) {
    const relevantChange = changes["ciiRangesCurrentYear"] || changes["ciiRating"] || changes["aer_gtnm"];
    if (relevantChange && this.allInputsAreDefined()) {
      this.setChartData();
      this.setChartOptions();
      this.setPlugins();
      this.chartReady = true;
    }
  }

  private allInputsAreDefined(): boolean {
    return this.ciiRangesCurrentYear != null;
  }

  setPlugins() {
    this.ciiRatingChartPlugins = [this.arrowPlugin, this.scaleLabelsPlugin(), ChartDataLabels];
    this.ciiRatingChartOptions = {
      ...this.ciiRatingChartOptions,
      plugins: {
        ...(this.ciiRatingChartOptions?.plugins ?? {}),
        arrowPointer: {
          ciiRating: this.ciiRating,
          labelBelow: this.aer_gtnm != null ? `${this.aer_gtnm.toFixed(1)} gCO\u2082/tnm` : "NV",
        },
      },
    };
  }

  setChartOptions() {
    this.ciiRatingChartOptions = {
      responsive: true,
      cutout: "70%",
      rotation: -90,
      circumference: 180,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          color: "#000",
          font: {
            weight: "normal",
          },
          formatter: (value: number, context: any) => {
            const labels = ["A", "B", "C", "D", "E"];
            return labels[context.dataIndex];
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: any) => {
              const ratingLetters = ["A", "B", "C", "D", "E"];
              const ranges = this.ciiRangesCurrentYear;
              const index = context.dataIndex;
              const label = ratingLetters[index];
              const [min, max] = ranges[label.toLowerCase() as keyof typeof ranges];
              return `${min.toFixed(1)} – ${max.toFixed(1)}`;
            },
          },
        },
      },
      animation: false,
      pluginsCustom: {
        index: this.getCiiIndexPosition(this.aer_gtnm),
      },
    };
  }
  setChartData() {
    this.ciiRatingChartData = {
      labels: ["A", "B", "C", "D", "E"],
      datasets: [
        {
          data: [
            this.ciiRangesCurrentYear.a[1] - this.ciiRangesCurrentYear.a[0],
            this.ciiRangesCurrentYear.b[1] - this.ciiRangesCurrentYear.b[0],
            this.ciiRangesCurrentYear.c[1] - this.ciiRangesCurrentYear.c[0],
            this.ciiRangesCurrentYear.d[1] - this.ciiRangesCurrentYear.d[0],
            this.ciiRangesCurrentYear.e[1] - this.ciiRangesCurrentYear.e[0],
          ],
          backgroundColor: ["#9BC56D", "#C6DEAB", "#FDE399", "#EE9DA3", "#E1535D"],
          borderWidth: 0,
        },
      ],
    };
  }
  arrowPlugin = {
    id: "arrowPointer",
    afterDatasetsDraw(chart: any, _args: any, opts: any) {
      const { ctx } = chart;
      const centerX = chart._metasets[0].data[0].x;
      const centerY = chart._metasets[0].data[0].y;
      const radius = chart._metasets[0].data[0].outerRadius;
      const anglePerPart = Math.PI / 5;
      const ciiRatingColors: Record<string, string> = {
        A: "#9BC56D",
        B: "#C6DEAB",
        C: "#FDE399",
        D: "#EE9DA3",
        E: "#E1535D",
      };

      const ciiRating: string = opts?.ciiRating ?? "NV";
      const aer: string = opts?.labelBelow ?? "";

      const drawArrow = (index: number, color: string) => {
        const angle = Math.PI + anglePerPart * index + anglePerPart / 2;
        const innerRadius = radius * 0.3;
        const baseWidth = radius * 0.08;
        const dirX = Math.cos(angle);
        const dirY = Math.sin(angle);
        const startX = centerX + innerRadius * dirX;
        const startY = centerY + innerRadius * dirY;
        const tipX = centerX + radius * dirX;
        const tipY = centerY + radius * dirY;
        const baseLeftX = startX + (baseWidth / 2) * -dirY;
        const baseLeftY = startY + (baseWidth / 2) * dirX;
        const baseRightX = startX + (baseWidth / 2) * dirY;
        const baseRightY = startY + (baseWidth / 2) * -dirX;

        ctx.beginPath();
        ctx.arc(startX, startY, baseWidth / 2, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(baseLeftX, baseLeftY);
        ctx.lineTo(baseRightX, baseRightY);
        ctx.lineTo(tipX, tipY);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      };
      const config = chart.options.pluginsCustom;
      if (config && config.index != null) {
        drawArrow(config.index, "#404040");
      }
      const letterColor = ciiRatingColors[ciiRating] || "#404040";
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.fillStyle = letterColor;
      ctx.font = `${radius * 0.3}px sans-serif`;
      ctx.fillText(ciiRating, centerX, centerY - radius * 0.1);

      if (aer) {
        ctx.fillStyle = "#404040";
        ctx.font = `${radius * 0.13}px sans-serif`;
        ctx.fillText(aer, centerX, centerY + radius * 0.2);
      }
      ctx.restore();
    },
  };

  scaleLabelsPlugin() {
    return {
      id: "scaleLabels",
      afterDatasetsDraw: (chart) => {
        const { ctx } = chart;
        const dataset = chart.getDatasetMeta(0).data;
        if (!dataset || !dataset.length) return;
        const centerX = dataset[0].x;
        const centerY = dataset[0].y;
        const radius = dataset[0].outerRadius;
        const ranges = this.ciiRangesCurrentYear;
        const boundaries: number[] = [ranges.a[0], ranges.a[1], ranges.b[1], ranges.c[1], ranges.d[1], ranges.e[1]];
        const globalMin = ranges.a[0];
        const globalMax = ranges.e[1];
        const totalScale = globalMax - globalMin;

        ctx.save();
        ctx.font = "12px Arial";
        ctx.fillStyle = "#333";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        boundaries.forEach((val) => {
          const frac = (val - globalMin) / totalScale;
          const angle = Math.PI + frac * Math.PI;
          const labelRadius = radius + 20 - 10 * Math.abs(Math.sin(angle));
          const x = centerX + labelRadius * Math.cos(angle);
          const y = centerY + labelRadius * Math.sin(angle);

          ctx.fillText(val.toFixed(1), x, y);
        });
        ctx.restore();
      },
    };
  }

  getCiiIndexPosition(value: number | null | undefined): number | null {
    if (value == null) {
      return null;
    }
    const ranges = this.ciiRangesCurrentYear;
    const globalMin = ranges.a[0];
    const globalMax = ranges.e[1];
    const clamped = Math.min(Math.max(value, globalMin), globalMax);
    const totalScale = globalMax - globalMin;
    const fraction = (clamped - globalMin) / totalScale;

    return -0.5 + fraction * 5;
  }
}
